import type { OnChainCifer } from '@/hooks/useCiferOnChain';

interface CiferEntry {
  id: number;
  status: 'pending' | 'delivered';
  tx_hash: string | null;
  delivered_at: number | null;
  created_at: number;
}

interface Props {
  /** Redeemed CIFERs from the Shield API (pending delivery tracking) */
  redeemed: CiferEntry[];
  /** On-chain CIFERs owned by the wallet (from CIFER SDK) */
  onChain: OnChainCifer[];
  /** Loading state for on-chain data */
  loading: boolean;
}

const gradients = [
  'from-purple-500 via-indigo-500 to-cyan-500',
  'from-rose-500 via-purple-500 to-indigo-500',
  'from-amber-500 via-orange-500 to-rose-500',
  'from-emerald-500 via-teal-500 to-cyan-500',
  'from-blue-500 via-violet-500 to-purple-500',
  'from-pink-500 via-rose-500 to-orange-500',
];

function OnChainCiferCard({ cifer }: { cifer: OnChainCifer }) {
  const idNum = Number(cifer.secretId);
  const gradient = gradients[idNum % gradients.length];
  const isReady = !cifer.isSyncing && cifer.publicKeyCid !== '';

  return (
    <div className="group relative">
      <div
        className={`relative overflow-hidden rounded-2xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${
          isReady
            ? 'border-border-subtle hover:border-purple-500/40 hover:shadow-purple-500/10'
            : 'border-amber-500/30 shadow-lg shadow-amber-500/10 animate-pulse-slow'
        }`}
      >
        {/* Visual header — tall/vertical */}
        <div className={`relative h-48 sm:h-56 bg-gradient-to-br ${gradient} overflow-hidden`}>
          {/* Decorative circles */}
          <div className="absolute inset-0 opacity-20">
            <div
              className="absolute w-32 h-32 rounded-full bg-white/20 blur-xl"
              style={{ top: `${(idNum * 37) % 60}%`, left: `${(idNum * 53) % 70}%` }}
            />
            <div
              className="absolute w-24 h-24 rounded-full bg-white/10 blur-lg"
              style={{ top: `${(idNum * 71) % 50}%`, left: `${(idNum * 23) % 60}%` }}
            />
          </div>

          {/* CIFER emblem */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="drop-shadow-lg">
              <path
                d="M32 4L8 16v16c0 16 24 28 24 28s24-12 24-28V16L32 4z"
                fill="white" fillOpacity="0.15" stroke="white" strokeWidth="1.5"
              />
              <path
                d="M32 18L22 32l10 14 10-14L32 18z"
                fill="white" fillOpacity="0.3" stroke="white" strokeWidth="1"
              />
              <circle cx="32" cy="32" r="3" fill="white" fillOpacity="0.8" />
            </svg>
          </div>

          {/* Secret ID badge */}
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-lg bg-black/30 backdrop-blur-sm text-white text-xs font-display font-bold">
            ID #{idNum}
          </div>

          {/* Status badge */}
          <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-lg backdrop-blur-sm text-xs font-display font-bold ${
            isReady
              ? 'bg-emerald-500/30 text-emerald-200'
              : 'bg-amber-500/30 text-amber-200'
          }`}>
            {isReady ? 'Active' : 'Syncing...'}
          </div>
        </div>

        {/* Info */}
        <div className="p-4 bg-glass-bg space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-display text-sm font-bold tracking-tight">CIFER</span>
            <span className="text-[10px] text-muted font-mono">
              ML-KEM-768
            </span>
          </div>

          {/* Encryption capacity (3GB per CIFER) */}
          <div>
            <div className="flex items-center justify-between text-xs text-muted mb-1">
              <span>Encryption capacity</span>
              <span className="font-display font-semibold text-gray-300">3 GB</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden bg-border-subtle">
              <div
                className="h-full rounded-full"
                style={{ width: '100%', background: 'linear-gradient(90deg, #A855F7, #6366F1)' }}
              />
            </div>
          </div>

          {/* Delegate info */}
          {cifer.delegate && cifer.delegate !== '0x0000000000000000000000000000000000000000' && (
            <div className="text-[10px] text-muted/60">
              Delegate: <span className="font-mono">{cifer.delegate.slice(0, 8)}...{cifer.delegate.slice(-6)}</span>
            </div>
          )}

          {/* Sell button — disabled, teaser */}
          <button
            disabled
            className="w-full mt-1 py-2 px-3 rounded-lg text-xs font-display font-semibold bg-white/[0.04] border border-border-subtle text-muted/40 cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="opacity-40">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Sell — $10
          </button>
        </div>
      </div>
    </div>
  );
}

function PendingRedeemCard({ cifer }: { cifer: CiferEntry }) {
  const date = new Date(cifer.created_at * 1000).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  return (
    <div className="group relative animate-pulse-slow">
      <div className="relative overflow-hidden rounded-2xl border border-purple-500/30 shadow-lg shadow-purple-500/10">
        <div className="relative h-48 sm:h-56 bg-gradient-to-br from-gray-700 via-gray-600 to-gray-700 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            <svg width="48" height="48" viewBox="0 0 64 64" fill="none" className="opacity-40">
              <path d="M32 4L8 16v16c0 16 24 28 24 28s24-12 24-28V16L32 4z"
                fill="white" fillOpacity="0.1" stroke="white" strokeWidth="1.5" />
              <path d="M32 18L22 32l10 14 10-14L32 18z"
                fill="white" fillOpacity="0.15" stroke="white" strokeWidth="1" />
            </svg>
          </div>
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-purple-500/30 backdrop-blur-sm text-purple-200 text-xs font-display font-bold">
            Awaiting delivery
          </div>
        </div>
        <div className="p-4 bg-glass-bg">
          <div className="flex items-center justify-between mb-1">
            <span className="font-display text-sm font-bold tracking-tight text-muted">CIFER</span>
            <span className="text-xs text-muted">{date}</span>
          </div>
          <span className="text-xs text-muted/60 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
            Redeemed — pending on-chain delivery
          </span>
        </div>
      </div>
    </div>
  );
}

export function CiferGallery({ onChain, redeemed, loading }: Props) {
  const pendingRedeems = redeemed.filter((c) => c.status === 'pending');
  const hasContent = onChain.length > 0 || pendingRedeems.length > 0;

  return (
    <section>
      <h2 className="section-label">Your CIFERs</h2>

      {/* Summary bar */}
      {onChain.length > 0 && (
        <div className="glass-card px-5 py-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8">
          <div className="flex items-center gap-2 text-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-purple-400">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span className="font-display font-semibold">{onChain.length}</span>
            <span className="text-muted">CIFER{onChain.length !== 1 ? 's' : ''} on-chain</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-cyan-400">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="font-display font-semibold">{onChain.length * 3} GB</span>
            <span className="text-muted">total encryption capacity</span>
          </div>
        </div>
      )}

      {loading && !hasContent && (
        <div className="glass-card p-8 sm:p-12 text-center">
          <div className="text-muted text-sm">Loading your CIFERs from chain...</div>
        </div>
      )}

      {!loading && !hasContent && (
        <div className="glass-card p-8 sm:p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-500/10 flex items-center justify-center">
            <svg width="32" height="32" viewBox="0 0 64 64" fill="none" className="text-purple-400">
              <path d="M32 4L8 16v16c0 16 24 28 24 28s24-12 24-28V16L32 4z"
                fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M32 18L22 32l10 14 10-14L32 18z"
                fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
            </svg>
          </div>
          <div className="font-display text-lg font-semibold mb-2">No CIFERs yet</div>
          <div className="text-sm text-muted max-w-md mx-auto">
            Earn 100 Shield points to redeem your first CIFER. Each CIFER gives you quantum-resistant encryption powered by ML-KEM-768.
          </div>
        </div>
      )}

      {hasContent && (
        <div className="flex gap-4">
          {/* Artefact video — matches card height */}
          <div className="hidden sm:flex flex-shrink-0 w-40 lg:w-48 rounded-2xl overflow-hidden border border-border-subtle">
            <video
              src="/artefact1.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>

          {/* CIFER cards grid — vertical cards */}
          <div className="flex-1 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* On-chain CIFERs first */}
            {onChain.map((cifer) => (
              <OnChainCiferCard key={cifer.secretId.toString()} cifer={cifer} />
            ))}
            {/* Pending redeems (not yet delivered on-chain) */}
            {pendingRedeems.map((cifer) => (
              <PendingRedeemCard key={cifer.id} cifer={cifer} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
